import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../components/AddLocalNotepad.css";

const API_BASE = "https://newerp.awasthivitality.com/api";

/* ===============================
   AUTH HELPERS
================================ */
const getSessionToken = () =>
  (localStorage.getItem("session_token") || "").trim();

const getAuthHeaders = () => {
  const token = getSessionToken();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Session-Token": token,
    Authorization: `Bearer ${token}`,
  };
};

const AddLocalNotepad = () => {
  const navigate = useNavigate();

  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);

  const [patientType, setPatientType] = useState("existing");
  const [newPatientName, setNewPatientName] = useState("");

  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    pdn_time: "",
    comments: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  /* ===============================
     FETCH PATIENTS
  ================================ */
  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoadingPatients(true);
      const res = await fetch(`${API_BASE}/patients/search`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      const arr = Array.isArray(data.data) ? data.data : [];
      setPatients(arr);
      setFilteredPatients(arr);
    } catch {
      setPatients([]);
      setFilteredPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  /* ===============================
     SEARCH (DEBOUNCE)
  ================================ */
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      searchPatientAPI(searchTerm);
    }, 300);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchTerm]);

  const searchPatientAPI = async (query) => {
    try {
      setLoadingPatients(true);
      const url = query.trim()
        ? `${API_BASE}/patients/search?q=${encodeURIComponent(query)}`
        : `${API_BASE}/patients/search`;

      const res = await fetch(url, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      setFilteredPatients(Array.isArray(data.data) ? data.data : []);
    } catch {
      setFilteredPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  /* ===============================
     SELECT / CLEAR PATIENT
  ================================ */
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm(patient.name);
    setShowDropdown(false);
  };

  const clearSelectedPatient = () => {
    setSelectedPatient(null);
    setSearchTerm("");
    setFilteredPatients(patients);
    setShowDropdown(true);
  };

  /* ===============================
     OUTSIDE CLICK
  ================================ */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchRef.current &&
        !searchRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  /* ===============================
     SAVE NOTE
  ================================ */
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (patientType === "existing" && !selectedPatient?.patient_id) {
      setMessage("❌ Please select a patient.");
      setMessageType("error");
      setLoading(false);
      return;
    }

    if (patientType === "new" && !newPatientName.trim()) {
      setMessage("❌ Please enter patient name.");
      setMessageType("error");
      setLoading(false);
      return;
    }

    const payload = {
      pdn_date: formData.date,
      pdn_time: formData.pdn_time,
      pdn_message: formData.comments,
    };

    let url = `${API_BASE}/notes/add`;

    if (patientType === "existing") {
      payload.patient_id = selectedPatient.patient_id;
      url = `${API_BASE}/patient/${selectedPatient.patient_id}/notes/add`;
    }

    if (patientType === "new") {
      payload.pdn_name = newPatientName;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setMessage("✔ Note saved successfully");
        setMessageType("success");
        setTimeout(() => navigate("/LocalNotepad"), 800);
      } else {
        setMessage("❌ " + (result.message || "Save failed"));
        setMessageType("error");
      }
    } catch {
      setMessage("❌ API error. Try again.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  /* ===============================
     UI
  ================================ */
  return (
    <div className="lnp-container">
      <div className="lnp-card">
        <h2 className="lnp-title">+ Add Local Note</h2>

        <div className="lnp-patient-type">
          <label>
            <input
              type="radio"
              value="existing"
              checked={patientType === "existing"}
              onChange={() => setPatientType("existing")}
            />
            Existing
          </label>

          <label>
            <input
              type="radio"
              value="new"
              checked={patientType === "new"}
              onChange={() => setPatientType("new")}
            />
            New
          </label>
        </div>

        {patientType === "existing" ? (
          <div className="lnp-search-box" ref={searchRef}>
            <input
              type="text"
              placeholder="Search Patient..."
              className="lnp-search-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
            />

            {selectedPatient && (
              <button className="lnp-clear-btn" onClick={clearSelectedPatient}>
                ✕
              </button>
            )}

            {showDropdown && (
              <div className="lnp-dropdown" ref={dropdownRef}>
                {loadingPatients ? (
                  <div className="lnp-loading">Loading...</div>
                ) : filteredPatients.length ? (
                  filteredPatients.map((p) => (
                    <div
                      key={p.patient_id}
                      className="lnp-item"
                      onClick={() => handlePatientSelect(p)}
                    >
                      <strong>{p.name}</strong>
                      <small>
                        PR: {p.pr_no} | {p.mobile}
                      </small>
                    </div>
                  ))
                ) : (
                  <div className="lnp-empty">No patients found</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <input
            type="text"
            placeholder="Enter New Patient Name"
            className="lnp-input"
            value={newPatientName}
            onChange={(e) => setNewPatientName(e.target.value)}
            style={{ marginBottom: "15px" }}
          />
        )}

        <form onSubmit={handleSave} className="lnp-form">
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="lnp-input"
            required
          />

          <input
            type="time"
            name="pdn_time"
            value={formData.pdn_time}
            onChange={handleChange}
            className="lnp-input"
            required
          />

          <input
            type="text"
            name="comments"
            value={formData.comments}
            placeholder="Comments"
            onChange={handleChange}
            className="lnp-input"
          />

          <button type="submit" disabled={loading} className="lnp-save-btn" style={{ width: "100%" }}>
            {loading ? "Saving..." : "Save"}
          </button>

          <button
            type="button"
            className="lnp-cancel-btn"
            onClick={() => navigate("/LocalNotepad")}
          >
            Cancel
          </button>

          {message && (
            <div className={`lnp-message ${messageType}`}>{message}</div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddLocalNotepad;